import React, { useState } from "react";
import { MuiThemeProvider } from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";

import {
  theme,
  darkTheme,
  highContrastLightTheme,
  highContrastDarkTheme,
} from "./themes";
import { logError } from "./utils/appUtils";

const ThemeContext = React.createContext();

const getInitialPalette = () => {
  const storedTheme = localStorage.getItem("theme");
  const defaultTheme =
    storedTheme === "light" || storedTheme === "dark" ? storedTheme : "dark";
  return defaultTheme;
};

const getInitialContrast = () => {
  const storedContrast = localStorage.getItem("contrast");
  const prefersHighContrast = window.matchMedia(
    "(prefers-contrast: high)",
  ).matches;

  let defaultContrast = "normal";
  if (storedContrast === "highContrast" || storedContrast === "normal") {
    defaultContrast = storedContrast;
  } else if (prefersHighContrast) {
    defaultContrast = "highContrast";
  }
  return defaultContrast;
};

// Helper function to get the correct theme based on combined themeState
export const getCombinedTheme = (palette, contrast) => {
  switch (true) {
    case palette === "light" && contrast === "normal":
      return theme;
    case palette === "light" && contrast === "highContrast":
      return highContrastLightTheme;
    case palette === "dark" && contrast === "normal":
      return darkTheme;
    case palette === "dark" && contrast === "highContrast":
      return highContrastDarkTheme;
    default:
      return theme;
  }
};

export const ThemeStateProvider = (props) => {
  // Call getInitialPalette once, lazily, to get the initial state.
  // getInitialPalette() calls the function immediately, on every render.
  const [palette, setPalette] = React.useState(getInitialPalette);
  const [contrast, setContrast] = useState(getInitialContrast);
  const [selectedTheme, setSelectedTheme] = useState(() => ({
    ...getCombinedTheme(palette, contrast),
  }));

  const togglePalette = () => {
    try {
      const newPalette = palette === "light" ? "dark" : "light";
      localStorage.setItem("theme", newPalette);
      setPalette(newPalette);
      setSelectedTheme({ ...getCombinedTheme(newPalette, contrast) });
    } catch (err) {
      logError("ThemeStateProvider:togglePalette", err, false);
    }
  };
  const toggleContrast = () => {
    try {
      const newContrast = contrast === "normal" ? "highContrast" : "normal";
      localStorage.setItem("contrast", newContrast);
      setContrast(newContrast);
      setSelectedTheme({ ...getCombinedTheme(palette, newContrast) });
    } catch (err) {
      logError("ThemeStateProvider:toggleContrast", err, false);
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        palette,
        contrast,
        selectedTheme,
        togglePalette,
        toggleContrast,
      }}
    >
      <MuiThemeProvider theme={selectedTheme}>
        <CssBaseline />
        {props.children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeState = () => React.useContext(ThemeContext);
